import { REPORT_TYPE_REGISTRY, type ReportTypeId } from '../../config/reportTypes';
import type { SiteRagReport } from '../../types/siteRag';
import type { OrbitSite } from './twoActDerivation';

export type PortfolioStation = 'stage1' | 'interlock' | 'stage2' | 'decided';

/** Display placement only. The report plan remains the readiness authority. */
export function portfolioStationOf(site: OrbitSite): PortfolioStation {
  if (site.stage.kind === 'decided') return 'decided';
  if (site.stage.kind === 'validating' && site.stage.needsScenario) return 'interlock';
  if (site.composite.decisionHold?.code === 'power-strategy-undeclared'
    && site.composite.decisionStages?.screening.presentTypes.includes('feasibility')) return 'interlock';
  return site.stage.kind === 'validating' ? 'stage2' : 'stage1';
}

function parseTimestampMs(value: string | null | undefined): number {
  const parsed = Date.parse(value ?? '');
  return Number.isFinite(parsed) ? parsed : 0;
}

function reportLatestRevisionMs(report: SiteRagReport): number {
  return Math.max(
    parseTimestampMs(report.createdAt),
    parseTimestampMs(report.updatedAt),
    parseTimestampMs(report.analysisRevision?.createdAt),
  );
}

function isNonGeneratableReportType(typeId: string | undefined): boolean {
  if (!typeId) return false;
  if (typeId === 'executive-summary') return true;
  return REPORT_TYPE_REGISTRY[typeId as ReportTypeId]?.status === 'retired';
}

export function latestSiteOutcome<
  T extends {
    siteId: string;
    reportTypeId?: string;
    status?: string;
    finishedAt: string;
  },
>(
  outcomes: readonly T[],
  siteId: string,
  options?: {
    reports?: readonly SiteRagReport[];
    presentTypes?: readonly string[];
  },
): T | undefined {
  const latestReportMsByType = new Map<string, number>();
  let latestSiteReportMs = 0;
  for (const report of options?.reports ?? []) {
    if (report.siteId !== siteId) continue;
    const type = report.reportType ?? 'feasibility';
    const revMs = reportLatestRevisionMs(report);
    latestReportMsByType.set(
      type,
      Math.max(latestReportMsByType.get(type) ?? 0, revMs),
    );
    if (!isNonGeneratableReportType(type)) {
      latestSiteReportMs = Math.max(latestSiteReportMs, revMs);
    }
  }
  const presentTypeSet = new Set(options?.presentTypes ?? []);

  // Group by reportTypeId first so an older failed run for a type is superseded
  // by a newer terminal run or a newer persisted report/revision for that type,
  // and exclude retired/non-generatable report types (which have no active pipeline writer).
  const latestByTypeKey = new Map<string, T>();
  let untypedSeq = 0;
  for (const outcome of outcomes) {
    if (outcome.siteId !== siteId) continue;
    const typeId = outcome.reportTypeId;
    if (isNonGeneratableReportType(typeId)) {
      continue;
    }
    const key = typeId ?? `__untyped_${++untypedSeq}`;
    const prev = latestByTypeKey.get(key);
    if (!prev || parseTimestampMs(outcome.finishedAt) >= parseTimestampMs(prev.finishedAt)) {
      latestByTypeKey.set(key, outcome);
    }
  }

  const actionable = [...latestByTypeKey.values()].filter((outcome) => {
    const typeId = outcome.reportTypeId;
    const outcomeMs = parseTimestampMs(outcome.finishedAt);
    if (!typeId) {
      if (outcome.status === 'failed' && latestSiteReportMs >= outcomeMs && latestSiteReportMs > 0) {
        return false;
      }
      return true;
    }
    const latestReportMs = latestReportMsByType.get(typeId) ?? 0;
    if (outcome.status === 'failed' && latestReportMs >= outcomeMs && latestReportMs > 0) {
      return false;
    }
    if (outcome.status === 'failed' && presentTypeSet.has(typeId) && latestReportMs === 0) {
      return false;
    }
    return true;
  });

  return actionable.sort(
    (a, b) => parseTimestampMs(b.finishedAt) - parseTimestampMs(a.finishedAt),
  )[0];
}
